import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, notification, Select, Button, Space } from 'antd';
import { useLocation } from "react-router-dom";
import { HiOutlineDocumentCheck } from "react-icons/hi2";
import { RiResetLeftFill } from "react-icons/ri";
import axios from 'axios';
import useSWR from 'swr';
import dayjs from 'dayjs';

interface MechanicOption {
    label: string;
    value: string;
    warehouse: string;
    status?: string;
}

const fetcher = (url: string) => axios.get(url).then((res) => res.data);
const withAllocateWorkOrderLogic = (Component: React.ComponentType<any>) => {
    return (props: any) => {
        const [form] = Form.useForm();
        const location = useLocation();
        const warehouseCode = location.state?.warehouse_code;
        const [highlightUnfinished, setHighlightUnfinished] = useState(false);
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [foremanOptions, setForemanOptions] = useState<{ label: string, value: string }[]>([]);
        const [tableItems, setTableItems] = useState<any[]>([]);
        const [mechanicOptions, setMechanicOptions] = useState<MechanicOption[]>([]);
        const [absentMechanics, setAbsentMechanics] = useState<Set<string>>(new Set());
        const [autosaveStatus, setAutosaveStatus] = useState<'stopped' | 'processing' | 'idle' | 'error'>('stopped');
        const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
        const workOrderNo = Form.useWatch('work_order_no', form);
        const serviceDate = Form.useWatch('service_date', form);
        const brand = Form.useWatch('brand', form);
        const foremanValue = Form.useWatch('foreman', form);
        const statusValue = Form.useWatch('status', form);

        const { data: mechanicData, error: mechanicError } = useSWR(
            foremanValue ? `${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/mechanic-list-by-foreman?foreman_code=${foremanValue}` : null,
            fetcher
        );

        useEffect(() => {
            if (mechanicData) {
                const options = mechanicData.map((item: any) => ({
                    label: item.name,
                    value: item.code,
                    warehouse: item.warehouse,
                    status: item.status || ''
                }));
                setMechanicOptions(options);
            } else {
                setMechanicOptions([]);
            }
        }, [mechanicData]);

        const handleActivityLog = async (status: string, mechanic: string) => {
            const currentWoNo = form.getFieldValue('work_order_no');
            if (!currentWoNo) return;

            const now = dayjs().format('DD/MM/YYYY HH:mm:ss');
            let startTime = null;
            let endTime = null;


            if (status === 'OP') {
                startTime = now;
            } else if (status === 'P' || status === 'QCP') {
                endTime = now;
            }

            try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/log-activity`, {
                    work_order_no: currentWoNo,
                    status: status,
                    technician_code: mechanic,
                    start_time: startTime,
                    end_time: endTime,
                    warehouse: selectedWarehouse
                });
            } catch (error) {
                console.error("Gagal mengirim log aktivitas:", error);
            }
        };

        const updateMechanicStatus = async (mechanicCode: string, status?: string) => {
            try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/update-mechanic-status`, {
                    mechanic_code: mechanicCode,
                    status: status || 'W'
                });
            } catch (error) {
                console.error("Gagal update status mekanik:", error);
            }
        }

        const handleReset = () => {
            const currentForeman = form.getFieldValue('foreman');
            form.resetFields();
            form.setFieldValue('foreman', currentForeman);
            form.setFieldValue('service_date', dayjs());
            setTableItems([]);
            setHighlightUnfinished(false);
            setAbsentMechanics(new Set());
        };

        const handleVoid = async () => {
            const currentWoNo = form.getFieldValue('work_order_no');

            if (!currentWoNo) {
                notification.warning({
                    message: 'Peringatan',
                    description: 'Tidak ada Work Order yang dipilih untuk dibatalkan',
                    placement: 'topRight'
                });
                return;
            }

            Modal.confirm({
                title: 'Konfirmasi Pembatalan',
                content: `Apakah Anda yakin ingin membatalkan Work Order ${currentWoNo}? Data akan dihapus dari sistem.`,
                okText: 'Ya',
                cancelText: 'Batal',
                onOk: async () => {
                    try {
                        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/delete`, {
                            data: { work_order_no: currentWoNo }
                        });

                        notification.success({
                            message: 'Berhasil',
                            description: `Work Order ${currentWoNo} telah dibatalkan dan dihapus dari sistem`
                        });

                        handleReset();
                    } catch (error) {
                        console.error("Gagal membatalkan work order:", error);
                        notification.error({
                            message: 'Gagal',
                            description: 'Gagal membatalkan Work Order. Silakan coba lagi.'
                        });
                    }
                }
            });
        };


        const handleSubmit = () => {
            const isAllMechanicsFilled = tableItems.length > 0 && tableItems.every(item =>
                item.service_status && item.service_status.trim() !== "" &&
                item.mechanic && item.mechanic.trim() !== ""
            );

            if (!isAllMechanicsFilled) {
                notification.error({
                    message: 'Peringatan',
                    description: 'Harap isi semua mekanik pada Operasi perbaikan',
                    placement: 'topRight'
                });
                return;
            }

            if (statusValue === '7' || statusValue === 'On Going') {
                const hasUnfinished = tableItems.some(item =>
                    item.service_status === 'OP' || item.service_status === 'P'
                );

                if (hasUnfinished) {
                    setHighlightUnfinished(true);
                    notification.error({
                        message: 'Peringatan',
                        description: 'Semua Operasi belum selesai !, harap lakukan QC Pass pada operasi di tabel',
                        placement: 'topRight'
                    });
                    return;
                }
                setHighlightUnfinished(false);
                Modal.confirm({
                    title: 'Konfirmasi QC Pass',
                    content: 'Semua operasi sudah selesai! apakah yakin untuk Melakukan QC Pass?',
                    okText: 'Ya',
                    cancelText: 'Batal',
                    onOk: async () => {
                        const finalStatusLabel = 'Waiting for Approved QC Pass';
                        form.setFieldsValue({ status: finalStatusLabel });

                        try {
                            setAutosaveStatus('processing');
                            const values = form.getFieldsValue();

                            const payload = {
                                status: '33',
                                service_date: values.service_date ? dayjs(values.service_date).format('YYYY-MM-DD') : null,
                                brand: values.brand,
                                work_order_no: values.work_order_no,
                                foreman: values.foreman,
                                service_advisor: values.service_advisor,
                                model: values.model,
                                chassis_no: values.chassis_no,
                                no_polisi: values.no_polisi,
                                customer_name: values.customer_name,
                                frt: values.frt,
                                warehouse: selectedWarehouse,
                                tableItems: tableItems.map((item: any) => ({
                                    operation_no: item.operation_no,
                                    operation_name: item.operation_name,
                                    frt: item.frt,
                                    service_status: item.service_status,
                                    mechanic: item.mechanic,
                                    warehouse: selectedWarehouse,
                                    status: item.status
                                }))
                            };

                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/save`, payload);
                            setAutosaveStatus('stopped');
                            notification.success({ message: 'Sukses', description: 'Berhasil!, Permintaan Approved QC Pass telah di kirimkan' });
                        } catch (error) {
                            console.error("Final save failed:", error);
                            setAutosaveStatus('error');
                            notification.error({ message: 'Gagal', description: 'Gagal memperbarui status ke QC Pass.' });
                        }
                    }
                });
            } else {
                form.setFieldsValue({ status: 'On Going' });
            }
        };

        const checkMechanicAttendance = async (mechanicCode: string, date: any) => {
            if (!date) {
                notification.warning({
                    message: 'Tanggal Service Belum Dipilih',
                    description: 'Silakan pilih tanggal service terlebih dahulu.',
                    placement: 'topRight'
                });
                return false;
            }

            const formattedDate = dayjs(date).format('DD/MM/YYYY');

            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/check-mechanic-attendance`, {
                    params: {
                        code_mechanic: mechanicCode,
                        date_trans: formattedDate
                    }
                });

                return response.data.isPresent;
            } catch (error) {
                console.error("Error checking mechanic attendance:", error);
                return false;
            }
        };

        const updateAbsentMechanics = async (date: any) => {
            if (!date) return;

            const formattedDate = dayjs(date).format('DD/MM/YYYY');
            const newAbsentMechanics = new Set<string>();

            for (const mechanicOption of mechanicOptions) {
                const isPresent = await checkMechanicAttendance(mechanicOption.value, date);
                if (!isPresent) {
                    newAbsentMechanics.add(mechanicOption.value);
                }
            }

            setAbsentMechanics(newAbsentMechanics);
        };

        useEffect(() => {
            if (mechanicOptions.length > 0 && serviceDate) {
                updateAbsentMechanics(serviceDate);
            }
        }, [mechanicOptions, serviceDate]);


        useEffect(() => {
            if (workOrderNo && (statusValue === '1' || statusValue === '7' || statusValue === 'Draft' || statusValue === 'On Going')) {
                const performAutosave = async () => {
                    setAutosaveStatus('processing');
                    await new Promise(resolve => setTimeout(resolve, 800));
                    try {
                        const values = form.getFieldsValue();

                        let dbAllocationStatus = values.status;
                        if (values.status === 'Draft') {
                            dbAllocationStatus = '1';
                        } else if (values.status === 'On Going') {
                            dbAllocationStatus = '7';
                        } else if (values.status === 'QC Pass') {
                            dbAllocationStatus = '6';
                        } else if (values.status === 'Waiting for Approved') {
                            dbAllocationStatus = '3';
                        } else if (values.status === 'Waiting for Approved QC Pass') {
                            dbAllocationStatus = '33';
                        } else if (values.status === 'Cancel') {
                            dbAllocationStatus = '5';
                        }

                        const payload = {
                            status: dbAllocationStatus,
                            service_date: values.service_date ? dayjs(values.service_date).format('YYYY-MM-DD') : null,
                            brand: values.brand,
                            work_order_no: values.work_order_no,
                            foreman: values.foreman,
                            service_advisor: values.service_advisor,
                            model: values.model,
                            chassis_no: values.chassis_no,
                            no_polisi: values.no_polisi,
                            customer_name: values.customer_name,
                            frt: values.frt,
                            warehouse: selectedWarehouse,
                            tableItems: tableItems.map(item => ({
                                operation_no: item.operation_no,
                                operation_name: item.operation_name,
                                frt: item.frt,
                                service_status: item.service_status,
                                mechanic: item.mechanic,
                                warehouse: selectedWarehouse,
                                status: item.status
                            }))
                        };

                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/save`, payload);
                        setAutosaveStatus('idle');
                    } catch (error) {
                        console.error("Autosave failed:", error);
                        setAutosaveStatus('error');
                    }
                };

                performAutosave();
            } else {
                setAutosaveStatus('stopped');
            }
        }, [workOrderNo, tableItems, form, selectedWarehouse, statusValue]);

        const { data: brandData } = useSWR(
            `${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/brand-list`,
            fetcher
        );
        const brandOptions = [
            { label: "- Select Brand -", value: "" },
            ...(brandData?.map((item: any) => ({
                label: item.description,
                value: item.code
            })) || [])
        ];
        const handleRowClick = async (record: any) => {
            try {
                const checkRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/check-exists`, {
                    params: { wo_no: record.work_order_no }
                });

                if (checkRes.data.exists) {
                    notification.warning({
                        message: 'Peringatan',
                        description: 'Data sudah di input!',
                        placement: 'topRight'
                    });
                    return;
                }
            } catch (error) {
                console.error("Error checking existence:", error);
            }
            form.setFieldsValue({
                work_order_no: record.work_order_no,
                service_advisor: record.service_advisor,
                model: record.model_description || record.model || "-",
                chassis_no: record.chassis_no,
                no_polisi: record.no_polisi,
                customer_name: record.bill_to_name,
                brand: record.brand,
            });
            setSelectedWarehouse(record.warehouse);
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/item-work-order`, {
                    params: { wo_no: record.work_order_no }
                });


                const mappedData = response.data.map((item: any) => ({
                    operation_no: item.item_code,
                    operation_name: item.item_name,
                    frt: item.qty,
                    service_status: '',
                    mechanic: '',
                    status: item.status
                }));

                setTableItems(mappedData);
            } catch (error) {
                console.error("Error fetching items:", error);
                setTableItems([]);
            }
            setIsModalOpen(false);
        };
        useEffect(() => {
            if (tableItems && tableItems.length > 0) {
                const totalFrt = tableItems.reduce((sum: number, item: any) => {
                    return sum + (Number(item.frt) || 0);
                }, 0);

                form.setFieldValue('frt', totalFrt);
            } else {
                form.setFieldValue('frt', 0);
            }
        }, [tableItems, form]);
        const Submitbtn = HiOutlineDocumentCheck as React.ComponentType<any>;
        const Resetbtn = RiResetLeftFill as React.ComponentType<any>;

        const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };
        const handleActiveOperation = (operationNo: string) => {
            Modal.confirm({
                title: 'Konfirmasi Aktivasi Operasi',
                content: 'Apakah Anda yakin ingin mengaktifkan kembali operasi ini?',
                okText: 'Ya',
                cancelText: 'Batal',
                onOk: async () => {
                    const currentWoNo = form.getFieldValue('work_order_no');
                    try {
                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/active-operation`, {
                            work_order_no: currentWoNo,
                            operation_no: operationNo
                        });
                        notification.success({
                            message: 'Berhasil',
                            description: `Operasi ${operationNo} berhasil diaktifkan kembali`
                        });
                        setTableItems(prev => prev.map(item => {
                            if (item.operation_no === operationNo) {
                                return { ...item, status: null };
                            }
                            return item;
                        }));
                    } catch (error) {
                        console.error("Gagal mengaktifkan operasi:", error);
                        notification.error({
                            message: 'Gagal',
                            description: 'Gagal mengaktifkan operasi. Silakan coba lagi.'
                        });
                    }
                }
            });
        };
        const handleCancelOperation = (operationNo: string) => {
            const isLast = tableItems.length === 1;
            const confirmContent = isLast ? (
                <span>
                    Operasi di batalkan sepenuhnya? tindakan ini akan menghapus daftar operasi dari tabel. <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>daftar operasi kosong, tindakan ini menghapus daftar alokasi !</span>
                </span>
            ) : (
                "Operasi di batalkan sepenuhnya? tindakan ini akan menghapus daftar operasi dari tabel"
            );

            Modal.confirm({
                title: 'Konfirmasi Pembatalan Operasi',
                content: confirmContent,
                okText: 'Yes',
                cancelText: 'No',
                onOk: async () => {
                    const currentWoNo = form.getFieldValue('work_order_no');
                    try {
                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/cancel-operation`, {
                            work_order_no: currentWoNo,
                            operation_no: operationNo,
                            is_last_item: isLast
                        });

                        notification.success({
                            message: 'Berhasil',
                            description: `Operasi ${operationNo} berhasil dibatalkan`
                        });

                        if (isLast) {
                            handleReset();
                        } else {
                            setTableItems(prev => prev.filter(item => item.operation_no !== operationNo));
                        }
                    } catch (error) {
                        console.error("Gagal membatalkan operasi:", error);
                        notification.error({
                            message: 'Gagal',
                            description: 'Gagal membatalkan operasi. Silakan coba lagi.'
                        });
                    }
                }
            });
        };
        const columns = [
            {
                title: 'Action',
                key: 'cancel_operation',
                render: (_: any, record: any) => (
                    <Space>
                        <Button
                            danger
                            size="small"
                            onClick={() => handleCancelOperation(record.operation_no)}
                            disabled={record.status === 'R'}
                        >
                            Cancel Operation
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => handleActiveOperation(record.operation_no)}
                            disabled={record.status !== 'R'}
                        >
                            Active Operation
                        </Button>
                    </Space>
                )
            },
            { title: 'Operation No', dataIndex: 'operation_no', key: 'operation_no' },
            { title: 'Operation Name', dataIndex: 'operation_name', key: 'operation_name' },
            { title: 'FRT', dataIndex: 'frt', key: 'frt' },
            {
                title: 'Service Status',
                dataIndex: 'service_status',
                key: 'service_status',
                render: (text: string, record: any, index: number) => (
                    <Select
                        placeholder="Select Status"
                        style={{ width: '100%' }}
                        value={text || undefined}
                        disabled={!record.mechanic || record.service_status === 'QCP' || record.status === 'R'}
                        onChange={async (value) => {
                            const handleStatusChange = () => {
                                const newItems = tableItems.map(item => item.status === 'R' ? item : { ...item, service_status: value });
                                setTableItems(newItems);
                                handleActivityLog(value, record.mechanic || '');
                                if (value === 'OP' || value === 'P') {
                                    const statusToSend = value === 'OP' ? 'W' : 'P';
                                    updateMechanicStatus(record.mechanic || '', statusToSend);
                                } else if (value === 'QCP') {
                                    if (record.mechanic) {
                                        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/clear-mechanic-status`, {
                                            mechanic_code: record.mechanic
                                        }).catch(error => {
                                            console.error("Failed to clear mechanic status:", error);
                                            notification.error({
                                                message: 'Error',
                                                description: 'Failed to clear mechanic status in database.'
                                            });
                                        });
                                    }
                                }
                            };

                            if (value === 'OP') {
                                form.setFieldValue('status', 'On Going');
                                handleStatusChange();
                            } else if (value === 'QCP') {
                                try {
                                    const currentWoNo = form.getFieldValue('work_order_no');
                                    const checkRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/check-supply-status`, {
                                        params: { wo_no: currentWoNo }
                                    });

                                    if (!checkRes.data.isReady) {
                                        Modal.error({
                                            title: 'Peringatan QC Pass',
                                            content: 'Tidak dapat QC Pass, Harap selesaikan supply slip pada item yang belum terpenuhi !',
                                        });
                                        return;
                                    }
                                } catch (error) {
                                    console.error("Validation error:", error);
                                    return;
                                }
                                let qcpRemark = '';
                                Modal.confirm({
                                    title: 'Konfirmasi QC Pass',
                                    content: (
                                        <div>
                                            <p>Apakah anda yakin untuk melakukan QC Pass? Pastikan tidak terlewatkan!</p>
                                            <Input.TextArea
                                                rows={3}
                                                placeholder="Remark QC Pass"
                                                onChange={(e) => { qcpRemark = e.target.value; }}
                                            />
                                        </div>
                                    ),
                                    okText: 'Ya',
                                    cancelText: 'Batal',
                                    onOk: async () => {
                                        if (record.mechanic) {
                                            try {
                                                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/clear-mechanic-status`, {
                                                    mechanic_code: record.mechanic
                                                });
                                            } catch (error) {
                                                console.error("Gagal mengosongkan status mekanik:", error);
                                                notification.error({
                                                    message: 'Error',
                                                    description: 'Gagal mengosongkan status mekanik di database.'
                                                });
                                            }
                                        }
                                        handleStatusChange();
                                        const currentWoNo = form.getFieldValue('work_order_no');
                                        const currentServiceAdvisor = form.getFieldValue('service_advisor');

                                        try {
                                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/update-service-status`, {
                                                work_order_no: currentWoNo,
                                                service_status: 'QCP'
                                            });
                                        } catch (error) {
                                            console.error("Gagal update service status ke database:", error);
                                            notification.error({
                                                message: 'Error',
                                                description: 'Gagal memperbarui service status di database.'
                                            });
                                        }

                                        try {
                                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/approvals/submit-qcpass`, {
                                                code: '02',
                                                no_document: currentWoNo,
                                                amount: '',
                                                request_by: currentServiceAdvisor,
                                                remark: 'QC PASS',
                                                service_qcp: '1',
                                                warehouse: selectedWarehouse
                                            });
                                        } catch (error) {
                                            console.error("Gagal mengirim data ke approval:", error);
                                            notification.error({
                                                message: 'Error',
                                                description: 'Gagal mengirim data QC Pass ke approval.'
                                            });
                                        }

                                        form.setFieldValue('status', 'Waiting for Approved QC Pass');
                                        try {
                                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/update-status`, {
                                                work_order_no: currentWoNo,
                                                status: '33'
                                            });

                                            Modal.success({
                                                content: 'Permintaan QC Pass berhasil di kirimkan menunggu persetujuan Approved !',
                                            });
                                        } catch (error) {
                                            console.error("Gagal update status ke database:", error);
                                            notification.error({
                                                message: 'Error',
                                                description: 'Gagal memperbarui status di database.'
                                            });
                                        }
                                    },
                                });
                            } else if (value === 'P') {
                                Modal.confirm({
                                    title: 'Konfirmasi',
                                    content: 'apakah anda yakin untuk menunda pekerjaan pada mekanik ini ?',
                                    okText: 'Ya',
                                    cancelText: 'Batal',
                                    onOk: handleStatusChange,
                                });
                            } else {
                                handleStatusChange();
                            }
                        }}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                        options={[
                            { label: 'On Proses', value: 'OP' },
                            { label: 'Paused', value: 'P', disabled: text !== 'OP' },
                            { label: 'QC Pass', value: 'QCP', disabled: statusValue === 'Draft' || statusValue === '1' },
                        ]}
                    />
                )
            },
            {
                title: 'Mechanic',
                dataIndex: 'mechanic',
                key: 'mechanic',
                render: (text: string, record: any, index: number) => (
                    <Select
                        showSearch
                        placeholder="Select Mechanic"
                        style={{ width: '100%' }}
                        value={text || undefined}
                        disabled={record.service_status === 'QCP' || record.status === 'R'}
                        optionFilterProp="label"
                        options={mechanicOptions
                            .filter(option => option.warehouse === selectedWarehouse)
                            .map(option => ({
                                ...option,
                                label: (() => {
                                    if (option.status === 'W') {
                                        return (
                                            <span style={{ color: 'black' }}>
                                                {option.label} <span style={{ color: 'green' }}>(Operation)</span>
                                            </span>
                                        );
                                    } else if (absentMechanics.has(option.value)) {
                                        return <span style={{ color: 'red' }}>{option.label} (Tidak Hadir)</span>;
                                    } else {
                                        return option.label;
                                    }
                                })(),
                                disabled: option.status === 'W'
                            }))}
                        onChange={async (value) => {
                            const selectedMechanic = mechanicOptions.find(opt => opt.value === value);
                            if (selectedMechanic && selectedMechanic.status === 'W') {
                                notification.warning({
                                    message: 'Mekanik sedang proses !',
                                    placement: 'topRight'
                                });
                                return;
                            }

                            const isPresent = await checkMechanicAttendance(value, form.getFieldValue('service_date'));

                            if (!isPresent) {
                                notification.warning({
                                    message: 'Mekanik tidak hadir !',
                                    placement: 'topRight'
                                });
                                return;
                            }

                            if (record.service_status === 'OP' && record.mechanic) {
                                notification.warning({
                                    message: 'Peringatan',
                                    description: 'Tidak dapat melakukan perubahan mekanik pada saat "On Proses", Ubah menjadi "Paused" jika hendak merubah mekanik',
                                    placement: 'topRight'
                                });
                                return;
                            }
                            const newItems = tableItems.map(item => ({ ...item, mechanic: value }));
                            setTableItems(newItems);
                        }}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                    />
                )
            },
        ];

        const { data: workOrderData, isLoading: isLoadingWorkOrder } = useSWR(
            serviceDate || brand || warehouseCode
                ? `${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/work-order-list?date=${serviceDate ? dayjs(serviceDate).format('DD/MM/YYYY') : ''}&brand=${brand || ''}&warehouse=${warehouseCode || ''}`
                : `${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/work-order-list?warehouse=${warehouseCode || ''}`,
            fetcher
        );

        useEffect(() => {
            const fetchMechanics = async () => {
                if (foremanValue) {
                    try {
                        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/mechanic-list-by-foreman`, {
                            params: { foreman_code: foremanValue }
                        });
                        const options = response.data.map((item: any) => ({
                            label: item.name,
                            value: item.code,
                            warehouse: item.warehouse,
                            status: item.status || ''
                        }));
                        setMechanicOptions(options);
                    } catch (error) {
                        console.error("Error fetching mechanic list:", error);
                    }
                } else {
                    setMechanicOptions([]);
                }
            };
            fetchMechanics();
        }, [foremanValue]);

        useEffect(() => {
            const fetchForeman = async () => {
                const warehouseCode = location.state?.warehouse_code || location.state?.record?.warehouse;
                if (warehouseCode) {
                    try {
                        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/foreman-list`, {
                            params: { warehouse_code: warehouseCode }
                        });
                        const options = response.data.map((item: any) => ({
                            label: item.name,
                            value: item.code
                        }));
                        setForemanOptions(options);
                    } catch (error) {
                        console.error("Error fetching foreman list:", error);
                    }
                }
            };
            fetchForeman();
        }, [location.state]);

        useEffect(() => {
            const loadInitialData = async () => {
                if (location.state?.record) {
                    const { record } = location.state;

                    form.setFieldsValue({
                        status: record.status === '1' ? 'Draft' :
                            record.status === '7' ? 'On Going' :
                                record.status === '6' ? 'QC Pass' :
                                    record.status === '3' ? 'Waiting for Approved' :
                                        record.status === '33' ? 'Waiting for Approved QC Pass' :
                                            record.status === '5' ? 'Cancel' : record.status,
                        service_date: record.service_date ? dayjs(record.service_date) : null,
                        brand: record.brand,
                        work_order_no: record.work_order_no,
                        foreman: record.foreman,
                        service_advisor: record.service_advisor,
                        model: record.model,
                        chassis_no: record.chassis_no,
                        no_polisi: record.no_polisi,
                        customer_name: record.customer_name,
                        frt: record.frt
                    });
                    setSelectedWarehouse(record.warehouse);

                    try {
                        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/saved-items`, {
                            params: { wo_no: record.work_order_no }
                        });
                        setTableItems(response.data);
                    } catch (error) {
                        console.error("Error fetching saved items:", error);
                    }
                } else if (location.state?.foreman) {
                    form.setFieldsValue({
                        foreman: location.state.foreman
                    });
                }
                if (!form.getFieldValue('service_date')) {
                    form.setFieldValue('service_date', dayjs());
                }
            };
            loadInitialData();
        }, [location.state, form]);

        return (
            <Component
                {...props}
                form={form}
                statusValue={statusValue}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                Submitbtn={Submitbtn}
                handleSubmit={handleSubmit}
                Resetbtn={Resetbtn}
                handleReset={handleReset}
                transformStyle={transformStyle}
                columns={columns}
                foremanOptions={foremanOptions}
                workOrderData={workOrderData}
                isLoadingWorkOrder={isLoadingWorkOrder}
                brandOptions={brandOptions}
                handleRowClick={handleRowClick}
                tableItems={tableItems}
                autosaveStatus={autosaveStatus}
                highlightUnfinished={highlightUnfinished}
                workOrderNo={workOrderNo}
                handleVoid={handleVoid}
            />
        );
    };
};

export default withAllocateWorkOrderLogic;